import { useDispatch } from 'react-redux'
import { setUserAlertRules } from '../redux/slices/users'
import {
  updateUserAlertRules as updateUserAlertRulesService,
  updateUserAlertRulesByDevice,
  updateUserAlertRulesByProject,
  updateUserAlertRulesByRuuviTag
} from '../services/userService'
import { Device, Project, RuuviTag, User, UserAlertRule, UserAlertRuleInput } from '../types'
import { AlertRuleName } from '../enums'

interface UserAlertRulesState {
  userAlertRulesByProject: UserAlertRule[]
  userAlertRulesByDevice: UserAlertRule[]
  userAlertRulesByRuuviTag: UserAlertRule[]
  updateUserAlertRules: (input: UserAlertRuleInput) => Promise<UserAlertRule[]>
  deleteUserAlertRule: (alertRuleName: AlertRuleName) => Promise<UserAlertRule[]>
}
interface UserAlertRulesProps {
  user: User
  userAlertRules: UserAlertRule[]
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  useRedux?: boolean
}

export default function useUserAlertRules({
  user,
  userAlertRules,
  project,
  device,
  ruuviTag,
  useRedux = true
}: UserAlertRulesProps): UserAlertRulesState {
  const dispatch = useDispatch()

  const userAlertRulesByProject = userAlertRules.filter(
    (userAlertRule) => project !== undefined && userAlertRule.projectId === project.id
  )

  const userAlertRulesByDevice = userAlertRules.filter(
    (userAlertRule) => device !== undefined && userAlertRule.deviceName === device.name
  )

  const userAlertRulesByRuuviTag = userAlertRules.filter(
    (userAlertRule) => ruuviTag !== undefined && userAlertRule.ruuviTagId === ruuviTag.id
  )

  function mapAsUserAlertRuleInputs(userAlertRules: UserAlertRule[]): UserAlertRuleInput[] {
    return userAlertRules.map((rule) => {
      const input: UserAlertRuleInput = {
        id: rule.id,
        ruleGroupName: rule.ruleGroupName,
        alertRuleName: rule.alertRuleName,
        emailNotificationEnabled: rule.emailNotificationEnabled,
        smsNotificationEnabled: rule.smsNotificationEnabled,
        appNotificationEnabled: rule.appNotificationEnabled
      }

      if (device) {
        input.deviceName = rule.deviceName
      } else if (ruuviTag) {
        input.ruuviTagId = rule.ruuviTagId
      } else {
        input.projectId = rule.projectId
      }

      return input
    })
  }

  function makeUserAlertRuleInputs(
    userAlertRules: UserAlertRule[],
    input?: UserAlertRuleInput
  ): UserAlertRuleInput[] {
    const inputs = mapAsUserAlertRuleInputs(userAlertRules)

    if (input) {
      // Check if the alert rule already exists.
      const index =
        input?.id !== undefined
          ? inputs.findIndex((rule) => rule.id === input.id)
          : inputs.findIndex((rule) => rule.alertRuleName === input.alertRuleName)

      if (index !== -1) {
        inputs[index] = input
      } else {
        inputs.push(input)
      }
    }

    return inputs
  }

  async function updateUserAlertRules(input: UserAlertRuleInput): Promise<UserAlertRule[]> {
    if (device) {
      return handleUpdateUserAlertRulesByDevice(device, input)
    } else if (ruuviTag) {
      return handleUpdateUserAlertRulesByRuuviTag(ruuviTag, input)
    } else if (project) {
      return handleUpdateUserAlertRulesByProject(project, input)
    } else {
      return updateUserAlertRulesService(user.id, makeUserAlertRuleInputs(userAlertRules, input))
    }
  }

  async function handleUpdateUserAlertRulesByProject(project: Project, input: UserAlertRuleInput) {
    const updatedUserAlertRules = await updateUserAlertRulesByProject(
      user.id,
      project.id,
      makeUserAlertRuleInputs(userAlertRulesByProject, input)
    )

    if (useRedux) {
      dispatch(
        setUserAlertRules([
          ...userAlertRules.filter((rule) => rule.projectId !== project.id),
          ...updatedUserAlertRules
        ])
      )
    }

    return updatedUserAlertRules
  }

  async function handleUpdateUserAlertRulesByDevice(device: Device, input: UserAlertRuleInput) {
    const updatedUserAlertRules = await updateUserAlertRulesByDevice(
      user.id,
      device.name,
      makeUserAlertRuleInputs(userAlertRulesByDevice, input)
    )

    if (useRedux) {
      dispatch(
        setUserAlertRules([
          ...userAlertRules.filter((rule) => rule.deviceName !== device.name),
          ...updatedUserAlertRules
        ])
      )
    }

    return updatedUserAlertRules
  }

  async function handleUpdateUserAlertRulesByRuuviTag(
    ruuviTag: RuuviTag,
    input: UserAlertRuleInput
  ) {
    const updatedUserAlertRules = await updateUserAlertRulesByRuuviTag(
      user.id,
      ruuviTag.id,
      makeUserAlertRuleInputs(userAlertRulesByRuuviTag, input)
    )

    if (useRedux) {
      dispatch(
        setUserAlertRules([
          ...userAlertRules.filter((rule) => rule.ruuviTagId !== ruuviTag.id),
          ...updatedUserAlertRules
        ])
      )
    }

    return updatedUserAlertRules
  }

  async function deleteUserAlertRule(alertRuleName: AlertRuleName) {
    if (device) {
      return handleDeleteUserAlertRulesByDevice(device, alertRuleName)
    } else if (ruuviTag) {
      return handleDeleteUserAlertRulesByRuuviTag(ruuviTag, alertRuleName)
    } else if (project) {
      return handleDeleteUserAlertRulesByProject(project, alertRuleName)
    } else {
      return updateUserAlertRulesService(
        user.id,
        makeUserAlertRuleInputs(
          userAlertRules.filter((rule) => rule.alertRuleName === alertRuleName)
        )
      )
    }
  }

  async function handleDeleteUserAlertRulesByProject(
    project: Project,
    alertRuleName: AlertRuleName
  ) {
    const updatedUserAlertRules = await updateUserAlertRulesByProject(
      user.id,
      project.id,
      makeUserAlertRuleInputs(
        userAlertRulesByProject.filter((rule) => rule.alertRuleName === alertRuleName)
      )
    )

    if (useRedux) {
      dispatch(
        setUserAlertRules([
          ...userAlertRules.filter((rule) => rule.projectId !== project.id),
          ...updatedUserAlertRules
        ])
      )
    }

    return updatedUserAlertRules
  }

  async function handleDeleteUserAlertRulesByDevice(device: Device, alertRuleName: AlertRuleName) {
    const updatedUserAlertRules = await updateUserAlertRulesByDevice(
      user.id,
      device.name,
      makeUserAlertRuleInputs(
        userAlertRulesByDevice.filter((rule) => rule.alertRuleName === alertRuleName)
      )
    )

    if (useRedux) {
      dispatch(
        setUserAlertRules([
          ...userAlertRules.filter((rule) => rule.deviceName !== device.name),
          ...updatedUserAlertRules
        ])
      )
    }

    return updatedUserAlertRules
  }

  async function handleDeleteUserAlertRulesByRuuviTag(
    ruuviTag: RuuviTag,
    alertRuleName: AlertRuleName
  ) {
    const updatedUserAlertRules = await updateUserAlertRulesByRuuviTag(
      user.id,
      ruuviTag.id,
      makeUserAlertRuleInputs(
        userAlertRulesByRuuviTag.filter((rule) => rule.alertRuleName === alertRuleName)
      )
    )

    if (useRedux) {
      dispatch(
        setUserAlertRules([
          ...userAlertRules.filter((rule) => rule.ruuviTagId !== ruuviTag.id),
          ...updatedUserAlertRules
        ])
      )
    }

    return updatedUserAlertRules
  }

  return {
    userAlertRulesByProject,
    userAlertRulesByDevice,
    userAlertRulesByRuuviTag,
    updateUserAlertRules,
    deleteUserAlertRule
  }
}
